import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const COURIER_COST = 99.00;

const Purchase = () => {
  const { user, token } = useAuth();
  const [purchaseType, setPurchaseType] = useState('digital');
  const [shippingAddress, setShippingAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!user) {
      toast.error('Teken asseblief eers aan om die boek te koop');
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  const basePrices = {
    digital: 99.00,
    physical: 320.00,
    both: 359.00
  };

  // Add courier cost for physical orders
  const getTotalPrice = (type) => {
    if (type === 'physical' || type === 'both') {
      return basePrices[type] + COURIER_COST;
    }
    return basePrices[type];
  };

  const handlePurchase = async () => {
    if ((purchaseType === 'physical' || purchaseType === 'both') && !shippingAddress) {
      toast.error('Verskaf asseblief u posadres');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/purchase/initiate`,
        {
          purchase_type: purchaseType,
          shipping_address: shippingAddress || null,
          phone_number: phoneNumber || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to PayFast payment page
      window.location.href = response.data.payment_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aankoop het misluk');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32 px-6 md:px-12">
      <div className="container mx-auto max-w-3xl">
        <h1 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-charcoal mb-8 text-center">
          Skaf die Boek aan
        </h1>
        <p className="text-center font-ui text-sm uppercase tracking-widest text-muted-charcoal mb-16">
          Kies u aankoopopsie
        </p>

        <div className="space-y-8">
          {/* Purchase Type Selection */}
          <div className="space-y-4">
            {/* Digital Option */}
            <div
              onClick={() => setPurchaseType('digital')}
              data-testid="option-digital"
              className={`bg-parchment border-2 p-8 cursor-pointer transition-all duration-300 ${
                purchaseType === 'digital' ? 'border-gold' : 'border-[#E5E0D6] hover:border-gold/50'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-heading text-2xl font-medium italic mb-2 text-charcoal">
                    Digitale Toegang
                  </h3>
                  <p className="font-body text-lg text-muted-charcoal">
                    Lees die volledige boek aanlyn in u persoonlike biblioteek
                  </p>
                </div>
                <p className="font-heading text-3xl text-gold">R{basePrices.digital.toFixed(2)}</p>
              </div>
            </div>

            {/* Physical Option */}
            <div
              onClick={() => setPurchaseType('physical')}
              data-testid="option-physical"
              className={`bg-parchment border-2 p-8 cursor-pointer transition-all duration-300 ${
                purchaseType === 'physical' ? 'border-gold' : 'border-[#E5E0D6] hover:border-gold/50'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-heading text-2xl font-medium italic mb-2 text-charcoal">
                    Fisiese Boek
                  </h3>
                  <p className="font-body text-lg text-muted-charcoal">
                    Ontvang 'n volkleur, gedrukte eksemplaar per koerier
                  </p>
                  <p className="font-body text-sm text-gold mt-2">
                    + R{COURIER_COST.toFixed(2)} koerierkoste
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-3xl text-gold">R{basePrices.physical.toFixed(2)}</p>
                  <p className="font-ui text-sm text-muted-charcoal">+ versending</p>
                </div>
              </div>
            </div>

            {/* Both Option */}
            <div
              onClick={() => setPurchaseType('both')}
              data-testid="option-both"
              className={`bg-parchment border-2 p-8 cursor-pointer transition-all duration-300 ${
                purchaseType === 'both' ? 'border-gold' : 'border-[#E5E0D6] hover:border-gold/50'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-heading text-2xl font-medium italic mb-2 text-charcoal">
                    Beide Digitaal + Fisies
                  </h3>
                  <p className="font-body text-lg text-muted-charcoal">
                    Digitale toegang asook 'n fisiese eksemplaar (beste waarde)
                  </p>
                  <p className="font-body text-sm text-gold mt-2">
                    + R{COURIER_COST.toFixed(2)} koerierkoste
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-3xl text-gold">R{basePrices.both.toFixed(2)}</p>
                  <p className="font-ui text-sm text-muted-charcoal">+ versending</p>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Details (if physical) */}
          {(purchaseType === 'physical' || purchaseType === 'both') && (
            <div className="bg-surface/30 border border-[#E5E0D6] p-8 space-y-6">
              <h3 className="font-heading text-xl font-medium italic text-charcoal mb-4">
                Versendingbesonderhede
              </h3>
              
              <div>
                <label className="block font-ui text-sm uppercase tracking-widest text-charcoal mb-2">
                  Posadres
                </label>
                <textarea
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  required
                  data-testid="shipping-address"
                  rows={4}
                  className="w-full bg-white border border-charcoal/20 focus:border-gold p-4 text-lg font-body placeholder:text-charcoal/40 outline-none transition-colors"
                  placeholder="Straat, Stad, Poskode"
                />
              </div>

              <div>
                <label className="block font-ui text-sm uppercase tracking-widest text-charcoal mb-2">
                  Telefoonnommer
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  data-testid="phone-number"
                  className="w-full bg-white border border-charcoal/20 focus:border-gold p-4 text-lg font-body placeholder:text-charcoal/40 outline-none transition-colors"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-charcoal/10 pt-4 mt-4">
                <div className="flex justify-between font-body text-muted-charcoal mb-2">
                  <span>Boekprys:</span>
                  <span>R{basePrices[purchaseType].toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-body text-muted-charcoal mb-2">
                  <span>Koerierkoste:</span>
                  <span>R{COURIER_COST.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-heading text-xl text-charcoal pt-2 border-t border-charcoal/10">
                  <span>Totaal:</span>
                  <span className="text-gold">R{getTotalPrice(purchaseType).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Purchase Button */}
          <div className="text-center pt-8">
            <button
              onClick={handlePurchase}
              disabled={loading}
              data-testid="purchase-button"
              className="bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-12 py-5 font-ui uppercase tracking-widest text-sm transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? 'Besig...' : `Betaal R${getTotalPrice(purchaseType).toFixed(2)}`}
            </button>
            <p className="font-body text-sm text-muted-charcoal mt-4">
              U sal na PayFast vir veilige betaling gelei word
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Purchase;
